/* ============================================================
   Luxe — theme.js
   Behaviours: reduced-motion guard, cart drawer, slide-in panel,
   homepage carousel, scroll parallax, count-up stats, variant
   selection, accordion, tabs, faceted filtering, predictive search,
   product recommendations, and the AJAX cart
   (Shopify Cart API + Section Rendering API).
   ============================================================ */
(function () {
  'use strict';

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  function reduced() { return prefersReduced.matches; }

  // Locale strings supplied by layout/theme.liquid (window.theme.strings).
  function t(key, fallback) {
    var st = window.theme && window.theme.strings;
    return (st && st[key]) || fallback;
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ----------------------------------------------------------
     Lenis smooth scroll (optional; mirrors PageLayout.tsx config)
     ---------------------------------------------------------- */
  function initLenis() {
    if (reduced() || typeof window.Lenis !== 'function') return;
    var lenis = new window.Lenis({
      duration: 1.15,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
    });
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    window.__vidaLenis = lenis;
  }

  /* ----------------------------------------------------------
     Cart drawer — port of Aside.tsx
     ---------------------------------------------------------- */
  var Drawer = {
    lastFocus: null,
    el: function () { return document.getElementById('CartDrawer'); },
    isOpen: function () {
      var d = this.el();
      return d && d.getAttribute('data-open') === 'true';
    },
    open: function () {
      var d = this.el();
      if (!d) return;
      this.lastFocus = document.activeElement;
      d.setAttribute('data-open', 'true');
      document.body.style.overflow = 'hidden';
      var focusable = d.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusable[0]) focusable[0].focus();
    },
    close: function () {
      var d = this.el();
      if (!d) return;
      d.setAttribute('data-open', 'false');
      // A slide-in panel may still be open behind the drawer — don't restore
      // page scroll while anything else is holding it locked.
      if (!Panel.anyOpen()) document.body.style.overflow = '';
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    },
    keepOpen: function () {
      // Re-assert open state after a section re-render, without stealing focus.
      var d = this.el();
      if (!d) return;
      d.setAttribute('data-open', 'true');
      document.body.style.overflow = 'hidden';
    },
    trap: function (e) {
      var d = this.el();
      if (!d || !this.isOpen()) return;
      if (e.key === 'Escape') { this.close(); return; }
      if (e.key !== 'Tab') return;
      var f = d.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    },
  };

  /* ----------------------------------------------------------
     Generic slide-in panel controller (mobile nav menu, etc.)
     Keyed by element id: [data-panel-open="<id>"] opens, any
     [data-panel-close] (or Escape / link click inside) closes.
     The cart drawer above keeps its own logic untouched.
     ---------------------------------------------------------- */
  var FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  var Panel = {
    lastFocus: null,
    open: function (id) {
      var el = document.getElementById(id);
      if (!el) return;
      this.lastFocus = document.activeElement;
      el.setAttribute('data-open', 'true');
      document.body.style.overflow = 'hidden';
      var trigger = document.querySelector('[data-panel-open="' + id + '"]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      var f = el.querySelector(FOCUSABLE);
      if (f) f.focus();
    },
    closeAll: function () {
      var open = document.querySelectorAll('[data-panel][data-open="true"]');
      if (!open.length) return;
      Array.prototype.forEach.call(open, function (el) {
        el.setAttribute('data-open', 'false');
        var trigger = document.querySelector('[data-panel-open="' + el.id + '"]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
      if (!Drawer.isOpen()) document.body.style.overflow = '';
      // Mirror Drawer: hand focus back to whatever opened the panel.
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
      this.lastFocus = null;
    },
    anyOpen: function () {
      return !!document.querySelector('[data-panel][data-open="true"]');
    },
    openEl: function () {
      return document.querySelector('[data-panel][data-open="true"]');
    },
    // Mirror of Drawer.trap — keeps Tab inside the open panel.
    trap: function (e) {
      var el = this.openEl();
      if (!el) return;
      if (e.key !== 'Tab') return;
      var f = el.querySelectorAll(FOCUSABLE);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    },
  };

  /* ----------------------------------------------------------
     AJAX cart — Shopify Cart API + Section Rendering API
     ---------------------------------------------------------- */
  var CART_SECTIONS = ['cart-drawer', 'header'];
  var cartBusy = false;

  function renderSections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      // The section's outer markup carries id="shopify-section-<id>".
      var target = document.getElementById('shopify-section-' + id);
      if (target) target.innerHTML = extractSectionInner(sections[id]);
    });
    // Re-wire behaviour bound at DOMContentLoaded — the replaced markup has no
    // listeners of its own (document-level delegated handlers survive, these don't).
    rewire();
  }

  // Section Rendering API returns the full section wrapper; strip it to inner HTML.
  function extractSectionInner(html) {
    var tmp = document.createElement('div');
    tmp.innerHTML = html.trim();
    var wrapper = tmp.querySelector('.shopify-section');
    return wrapper ? wrapper.innerHTML : tmp.innerHTML;
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().then(function (data) {
        // Cart API errors (e.g. 422 out-of-stock) return a JSON body with a
        // description but no `sections`. Reject so callers can surface it.
        if (!r.ok) {
          var err = new Error((data && (data.description || data.message)) || t('cartError', 'Cart error'));
          err.data = data;
          err.status = r.status;
          throw err;
        }
        return data;
      });
    });
  }

  // Cart error surface — rendered into the drawer, cleared on the next success.
  function showCartError(message) {
    var d = document.getElementById('CartDrawer');
    if (!d) return;
    var box = d.querySelector('[data-cart-error]');
    if (!box) return;
    box.textContent = message || '';
    box.hidden = !message;
  }

  function cartAdd(id, quantity) {
    return postJSON('/cart/add.js', {
      items: [{ id: id, quantity: quantity || 1 }],
      sections: CART_SECTIONS,
    }).then(function (res) {
      if (res && res.sections) renderSections(res.sections);
      showCartError('');
      Drawer.open();
      return res;
    }).catch(function (err) {
      Drawer.open();
      showCartError(err && err.message);
      throw err;
    });
  }

  function cartChange(line, quantity) {
    var wasOpen = Drawer.isOpen();
    return postJSON('/cart/change.js', {
      line: line,
      quantity: quantity,
      sections: CART_SECTIONS,
    }).then(function (res) {
      if (res && res.sections) renderSections(res.sections);
      showCartError('');
      if (wasOpen) Drawer.keepOpen();
      return res;
    }).catch(function (err) {
      if (wasOpen) Drawer.keepOpen();
      showCartError(err && err.message);
      throw err;
    });
  }

  /* ----------------------------------------------------------
     Homepage carousel — port of _index.tsx:50-60,105
     ---------------------------------------------------------- */
  function initCarousel() {
    var root = document.querySelector('[data-carousel]');
    if (!root || root.__wired) return;
    root.__wired = true;
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-slide]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-carousel-dot]'));
    var counter = root.querySelector('[data-carousel-counter]');
    var n = slides.length;
    if (n === 0) return;
    var active = 0;
    var timer = null;

    function setText(sel, val) {
      var el = root.querySelector(sel);
      if (el && val != null) el.textContent = val;
    }
    function paint() {
      slides.forEach(function (s, i) {
        var on = i === active;
        s.style.display = on ? 'block' : 'none';
        // Hide inactive slides from AT so their links aren't tabbable.
        if (on) { s.removeAttribute('aria-hidden'); } else { s.setAttribute('aria-hidden', 'true'); }
      });
      dots.forEach(function (d, i) {
        var on = i === active;
        d.style.background = on ? 'var(--accent)' : 'var(--line)';
        if (on) { d.setAttribute('aria-current', 'true'); } else { d.removeAttribute('aria-current'); }
      });
      if (counter) counter.textContent = String(active + 1).padStart(2, '0') + ' / ' + String(n).padStart(2, '0');
      // Shared overlay chrome (name plate, N°, spec strip) from the active slide.
      var cur = slides[active];
      var d = cur.dataset;
      setText('[data-slide-name]', d.name);
      setText('[data-slide-line]', d.line);
      if (d.no != null) setText('[data-slide-no]', 'N° ' + d.no + (d.edition ? ' / ' + d.edition : ''));
      ['badge_1', 'badge_2', 'length', 'price'].forEach(function (k) {
        setText('[data-slide-spec="' + k + '"]', d[k === 'badge_1' ? 'badge1' : k === 'badge_2' ? 'badge2' : k]);
      });
    }
    function go(i) {
      active = ((i % n) + n) % n;
      paint();
      restart();
    }
    // Autoplay is opt-out for everyone (pause button) and off by default for
    // anyone who asked for reduced motion — WCAG 2.2.2.
    var paused = reduced();
    var toggle = root.querySelector('[data-carousel-toggle]');

    function start() { if (n > 1 && !paused) timer = setInterval(function () { go(active + 1); }, 5000); }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function restart() { stop(); start(); }

    function setPaused(v) {
      paused = v;
      if (toggle) {
        toggle.textContent = v ? t('play', 'Play') : t('pause', 'Pause');
        toggle.setAttribute('aria-label', v ? t('play', 'Play slideshow') : t('pause', 'Pause slideshow'));
        toggle.setAttribute('aria-pressed', v ? 'true' : 'false');
      }
      if (v) { stop(); } else { start(); }
    }

    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-carousel-toggle]')) { setPaused(!paused); return; }
      var prev = e.target.closest('[data-carousel-prev]');
      var next = e.target.closest('[data-carousel-next]');
      var dot = e.target.closest('[data-carousel-dot]');
      if (prev) go(active - 1);
      else if (next) go(active + 1);
      else if (dot) go(dots.indexOf(dot));
    });

    // Pause while the shopper is interacting with the carousel.
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', function () { if (!paused) start(); });
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', function (e) {
      if (!paused && !root.contains(e.relatedTarget)) start();
    });

    paint();
    setPaused(paused);
  }

  /* ----------------------------------------------------------
     Scroll parallax — port of _index.tsx:72-98
     Each [data-parallax-scope] drives its descendant [data-parallax]
     elements. data-px-mult = multiplier, data-px-mode = pct|px,
     data-px-base = optional translate base (e.g. -50%).
     ---------------------------------------------------------- */
  function initParallax() {
    if (reduced()) return;
    if (window.innerWidth < 768) return; // mobile: layout is stacked, no parallax
    var scopes = Array.prototype.slice.call(document.querySelectorAll('[data-parallax-scope]'));
    if (!scopes.length) return;

    function progressOf(el) {
      var r = el.getBoundingClientRect();
      var vh = window.innerHeight;
      return Math.max(-1, Math.min(1, ((r.top + r.height / 2 - vh / 2) / (vh + r.height)) * 2));
    }
    function tick() {
      scopes.forEach(function (scope) {
        var p = progressOf(scope);
        var layers = scope.querySelectorAll('[data-parallax]');
        Array.prototype.forEach.call(layers, function (layer) {
          var mult = parseFloat(layer.getAttribute('data-px-mult')) || 0;
          var mode = layer.getAttribute('data-px-mode') || 'pct';
          var base = layer.getAttribute('data-px-base');
          if (mode === 'px') {
            var px = (p * mult).toFixed(1);
            layer.style.transform = base
              ? 'translateY(calc(' + base + ' + ' + px + 'px))'
              : 'translate3d(0,' + px + 'px,0)';
          } else {
            layer.style.transform = 'translate3d(0,' + (p * mult).toFixed(2) + '%,0)';
          }
        });
      });
    }
    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', tick, { passive: true });
    tick();
  }

  /* ----------------------------------------------------------
     Count-up stats — port of useCountUp.ts
     ---------------------------------------------------------- */
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  function initCountUp() {
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-countup]'));
    if (!els.length) return;

    els.forEach(function (el) {
      var target = parseFloat(el.getAttribute('data-countup-target')) || 0;
      var pad = parseInt(el.getAttribute('data-countup-pad'), 10) || 0;
      function render(v) {
        var s = String(v);
        el.textContent = pad ? s.padStart(pad, '0') : s;
      }
      if (reduced()) { render(target); return; }
      render(0);

      var started = false, startT = null, raf = null;
      function frame(now) {
        if (startT === null) startT = now;
        var t = Math.min(1, (now - startT) / 1500);
        render(Math.round(easeOutCubic(t) * target));
        if (t < 1) raf = requestAnimationFrame(frame);
      }
      function check() {
        if (started) return;
        var r = el.getBoundingClientRect();
        var vh = window.innerHeight;
        if (r.top < vh * 0.84 && r.top > -300) {
          started = true;
          raf = requestAnimationFrame(frame);
          window.removeEventListener('scroll', check);
        }
      }
      window.addEventListener('scroll', check, { passive: true });
      check();
    });
  }

  /* ----------------------------------------------------------
     Product variant selection — port of VariantOptions.tsx logic
     ---------------------------------------------------------- */
  function initVariantSelectors() {
    var roots = document.querySelectorAll('[data-variant-root]');
    Array.prototype.forEach.call(roots, function (root) { setupVariantRoot(root); });
  }

  function setupVariantRoot(root) {
    if (root.__wired) return;
    var mapEl = root.querySelector('[data-variant-map]');
    if (!mapEl) return;
    root.__wired = true;
    var map, variants, optionNames;
    try { map = JSON.parse(mapEl.textContent); } catch (e) { return; }
    variants = map.variants || [];
    optionNames = map.option_names || [];

    var selected = {};
    root.querySelectorAll('[data-option-name]').forEach(function (group) {
      var on = group.querySelector('.opt-on');
      if (on) selected[group.getAttribute('data-option-name')] = on.getAttribute('data-option-value');
    });

    function findVariant(sel) {
      var want = sel || selected;
      return variants.find(function (v) {
        return v.options.every(function (val, i) {
          return want[optionNames[i]] === val;
        });
      });
    }

    // Grey out option values that can't be combined with the current selection,
    // instead of only disabling the Add button after the fact.
    function markUnavailable() {
      root.querySelectorAll('[data-option-name]').forEach(function (group) {
        var name = group.getAttribute('data-option-name');
        group.querySelectorAll('[data-option-value]').forEach(function (btn) {
          var probe = {};
          Object.keys(selected).forEach(function (k) { probe[k] = selected[k]; });
          probe[name] = btn.getAttribute('data-option-value');
          var match = findVariant(probe);
          var usable = !!(match && match.available);
          btn.setAttribute('aria-disabled', usable ? 'false' : 'true');
          btn.style.opacity = usable ? '' : '.4';
          btn.style.textDecoration = match ? '' : 'line-through';
        });
      });
    }

    function apply() {
      var v = findVariant();
      var idInput = root.querySelector('[data-variant-id]');
      var priceEl = root.querySelector('[data-variant-price]');
      var addBtn = root.querySelector('[data-add-button]');
      if (v) {
        if (idInput) idInput.value = v.id;
        if (priceEl) priceEl.textContent = formatMoney(v.price);
        if (addBtn) {
          addBtn.disabled = !v.available;
          addBtn.setAttribute('data-variant-id', v.id);
          updateAddLabel(addBtn, v);
        }
      } else if (addBtn) {
        addBtn.disabled = true;
      }
      markUnavailable();
    }

    root.addEventListener('click', function (e) {
      var opt = e.target.closest('[data-option-value]');
      if (!opt) return;
      e.preventDefault();
      var group = opt.closest('[data-option-name]');
      var name = group.getAttribute('data-option-name');
      selected[name] = opt.getAttribute('data-option-value');
      group.querySelectorAll('[data-option-value]').forEach(function (o) {
        var on = o === opt;
        o.classList.toggle('opt-on', on);
        o.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      var label = group.querySelector('[data-option-label]');
      if (label) label.textContent = name + ' — ' + selected[name];
      apply();
    });

    // qty stepper
    root.addEventListener('click', function (e) {
      var dec = e.target.closest('[data-qty-dec]');
      var inc = e.target.closest('[data-qty-inc]');
      if (!dec && !inc) return;
      var field = root.querySelector('[data-qty-value]');
      var input = root.querySelector('[data-qty-input]');
      var q = parseInt(field.textContent, 10) || 1;
      q = dec ? Math.max(1, q - 1) : Math.min(9, q + 1);
      field.textContent = q;
      if (input) input.value = q;
      var addBtn = root.querySelector('[data-add-button]');
      var v = findVariant();
      if (addBtn && v) updateAddLabel(addBtn, v);
    });

    apply();
  }

  function updateAddLabel(btn, v) {
    var labelEl = btn.querySelector('[data-add-label]');
    if (!labelEl) return;
    var root = btn.closest('[data-variant-root]');
    var field = root && root.querySelector('[data-qty-value]');
    var qty = field ? (parseInt(field.textContent, 10) || 1) : 1;
    if (!v.available) { labelEl.textContent = t('soldOut', 'Sold out'); return; }
    labelEl.textContent = t('addToCart', 'Add to cart') + ' — ' + formatMoney(v.price * qty);
  }

  /* Mirror of Liquid's `money` filter, driven by the shop's own money_format
     (window.theme.moneyFormat). Shopify.formatMoney only exists when the shop
     loads option_selection.js, which this theme doesn't — so relying on it meant
     JS-rendered prices silently fell back to a different format than the
     server-rendered ones. Takes cents. */
  function formatMoney(cents, format) {
    var fmt = format || (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var value = typeof cents === 'string' ? Number(cents.replace(/[^0-9.-]/g, '')) : Number(cents);
    if (!isFinite(value)) return '';

    function group(num, decimals, thousandSep, decimalSep) {
      var n = Math.abs(num / 100).toFixed(decimals);
      var parts = n.split('.');
      var intPart = parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1' + thousandSep);
      var out = parts[1] ? intPart + decimalSep + parts[1] : intPart;
      return (num < 0 ? '-' : '') + out;
    }

    var m = /\{\{\s*(\w+)\s*\}\}/.exec(fmt);
    var placeholder = m ? m[1] : 'amount';
    var out;
    switch (placeholder) {
      case 'amount_no_decimals':                      out = group(value, 0, ',', '.'); break;
      case 'amount_with_comma_separator':             out = group(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = group(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator':        out = group(value, 2, "'", '.'); break;
      case 'amount_with_space_separator':             out = group(value, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': out = group(value, 0, ' ', ','); break;
      default:                                        out = group(value, 2, ',', '.');
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
  }

  /* /search/suggest.json returns prices as decimals in the shop's currency
     (e.g. 885.00) with no pre-formatted variant, so convert to cents and run
     them through the same formatter the rest of the theme uses. */
  function formatSuggestPrice(value) {
    if (value === null || value === undefined || value === '') return '';
    var amount = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
    if (!isFinite(amount)) return '';
    return formatMoney(Math.round(amount * 100));
  }

  /* ----------------------------------------------------------
     Accordion — port of ProductAccordion.tsx (max-height toggle)
     ---------------------------------------------------------- */
  function initAccordions() {
    document.querySelectorAll('[data-accordion]').forEach(function (acc) {
      if (acc.__wired) return;
      acc.__wired = true;
      acc.addEventListener('click', function (e) {
        var head = e.target.closest('[data-acc-head]');
        if (!head) return;
        var row = head.closest('[data-acc-row]');
        var panel = row.querySelector('[data-acc-panel]');
        var open = row.getAttribute('data-open') === 'true';
        // close siblings
        acc.querySelectorAll('[data-acc-row]').forEach(function (r) {
          r.setAttribute('data-open', 'false');
          var p = r.querySelector('[data-acc-panel]');
          if (p) p.style.maxHeight = '0px';
          var sign = r.querySelector('[data-acc-sign]');
          if (sign) sign.textContent = '+';
          // Reset the sibling's own aria-expanded, or multiple headers end up
          // reporting themselves as expanded to assistive tech.
          var h = r.querySelector('[data-acc-head]');
          if (h) h.setAttribute('aria-expanded', 'false');
        });
        if (!open) {
          row.setAttribute('data-open', 'true');
          // Measure instead of assuming a fixed height — long copy was clipping.
          if (panel) panel.style.maxHeight = panel.scrollHeight + 'px';
          var s = row.querySelector('[data-acc-sign]');
          if (s) s.textContent = '–';
          head.setAttribute('aria-expanded', 'true');
        } else {
          head.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  /* ----------------------------------------------------------
     Faceted filtering — progressive enhancement over the GET form.
     Without JS the form submits normally; with JS we fetch the same
     URL and swap the results + facets in, keeping history in sync.
     ---------------------------------------------------------- */
  var facetsBound = false;

  function initFacets() {
    var form = document.querySelector('[data-facet-form]');
    // Document-level listeners must only ever be registered once, no matter
    // how often the section re-renders.
    if (!form || facetsBound) return;

    var resultsEl = document.querySelector('[data-facet-results]');
    if (!resultsEl) return;
    facetsBound = true;
    var pending = null;

    function apply(url, push) {
      if (pending) pending.abort();
      pending = typeof AbortController !== 'undefined' ? new AbortController() : null;
      resultsEl.setAttribute('aria-busy', 'true');

      fetch(url, pending ? { signal: pending.signal } : undefined)
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (html) {
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');

          var freshResults = doc.querySelector('[data-facet-results]');
          if (freshResults) resultsEl.innerHTML = freshResults.innerHTML;

          // Facet counts and active chips change with the result set.
          var freshForm = doc.querySelector('[data-facet-form]');
          var liveForm = document.querySelector('[data-facet-form]');
          if (freshForm && liveForm) liveForm.innerHTML = freshForm.innerHTML;

          var freshCount = doc.querySelector('[data-facet-count]');
          var liveCount = document.querySelector('[data-facet-count]');
          if (freshCount && liveCount) liveCount.textContent = freshCount.textContent;

          if (push) history.pushState({ facets: url }, '', url);
          rewire();
        })
        .catch(function () { /* aborted or offline — leave current results */ })
        .then(function () { resultsEl.removeAttribute('aria-busy'); });
    }

    function urlFromForm() {
      var liveForm = document.querySelector('[data-facet-form]');
      var qs = new URLSearchParams(new FormData(liveForm));
      // Drop empty price inputs so they don't become filter.v.price.gte=
      var clean = new URLSearchParams();
      qs.forEach(function (v, k) { if (v !== '') clean.append(k, v); });
      var base = liveForm.getAttribute('action') || window.location.pathname;
      var s = clean.toString();
      return s ? base + '?' + s : base;
    }

    // Checkbox/price changes and Apply clicks
    document.addEventListener('change', function (e) {
      if (!e.target.closest('[data-facet-form]')) return;
      apply(urlFromForm(), true);
    });

    document.addEventListener('submit', function (e) {
      if (!e.target.closest('[data-facet-form]')) return;
      e.preventDefault();
      apply(urlFromForm(), true);
    });

    // Chip removal / clear-all links inside the facet form
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-facet-form] a[href]');
      if (!link) return;
      e.preventDefault();
      apply(link.getAttribute('href'), true);
    });

    window.addEventListener('popstate', function () {
      apply(window.location.href, false);
    });
  }

  /* ----------------------------------------------------------
     Product recommendations — Section Rendering API
     ---------------------------------------------------------- */
  function initRecommendations() {
    document.querySelectorAll('[data-recommendations]').forEach(function (el) {
      if (el.__wired) return;
      el.__wired = true;
      var url = el.getAttribute('data-url');
      if (!url) return;

      fetch(url)
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (html) {
          if (!html) return;
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          var fresh = tmp.querySelector('[data-recommendations]');
          // Empty response (no recommendations for this product) leaves the
          // placeholder alone, so nothing renders rather than an empty heading.
          if (fresh && fresh.innerHTML.trim()) el.innerHTML = fresh.innerHTML;
        })
        .catch(function () { /* leave the section empty */ });
    });
  }

  /* ----------------------------------------------------------
     Predictive search — /search/suggest.json
     ---------------------------------------------------------- */
  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function initPredictiveSearch() {
    document.querySelectorAll('[data-predictive-search]').forEach(function (root) {
      if (root.__wired) return;
      root.__wired = true;

      var input = root.querySelector('[data-predictive-input]');
      var box = root.querySelector('[data-predictive-results]');
      var status = root.querySelector('[data-predictive-status]');
      if (!input || !box) return;

      var controller = null;
      var activeIndex = -1;

      function close() {
        box.hidden = true;
        box.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
        activeIndex = -1;
      }

      function options() {
        return Array.prototype.slice.call(box.querySelectorAll('[role="option"]'));
      }

      function highlight(i) {
        var opts = options();
        if (!opts.length) return;
        activeIndex = (i + opts.length) % opts.length;
        opts.forEach(function (o, n) {
          var on = n === activeIndex;
          o.setAttribute('aria-selected', on ? 'true' : 'false');
          o.style.background = on ? 'var(--paper-2)' : '';
          if (on) {
            input.setAttribute('aria-activedescendant', o.id);
            o.scrollIntoView({ block: 'nearest' });
          }
        });
      }

      function render(data, term) {
        var res = (data && data.resources && data.resources.results) || {};
        var groups = [
          { key: 'products', label: t('products', 'Products') },
          { key: 'collections', label: t('collections', 'Collections') },
          { key: 'pages', label: t('pages', 'Pages') },
          { key: 'articles', label: t('articles', 'Articles') },
        ];
        var html = '';
        var count = 0;

        groups.forEach(function (g) {
          var items = res[g.key];
          if (!items || !items.length) return;
          html += '<div class="mlab" style="padding:12px 16px 6px;color:var(--ink-muted);">' + g.label + '</div>';
          items.forEach(function (item) {
            var id = 'ps-' + g.key + '-' + count;
            var img = item.featured_image && item.featured_image.url
              ? '<img src="' + item.featured_image.url + '" alt="" width="44" height="44" loading="lazy" style="width:44px;height:44px;object-fit:cover;flex-shrink:0;">'
              : '';
            var priceText = formatSuggestPrice(item.price);
            var price = priceText ? '<span style="margin-left:auto;font-family:var(--font-display);font-size:15px;white-space:nowrap;">' + priceText + '</span>' : '';
            html += '<a role="option" id="' + id + '" aria-selected="false" href="' + item.url + '"'
              + ' style="display:flex;align-items:center;gap:12px;padding:10px 16px;text-decoration:none;color:inherit;border-top:1px solid var(--line);">'
              + img
              + '<span style="font-family:var(--font-display);font-size:16px;line-height:1.2;">' + item.title + '</span>'
              + price
              + '</a>';
            count++;
          });
        });

        if (!count) {
          var noMatch = t('noMatches', 'No matches for \u201c@@\u201d').replace('@@', term);
          html = '<div style="padding:18px 16px;font-family:var(--font-sans);font-size:14px;color:var(--ink-soft);">' + noMatch + '</div>';
        }

        box.innerHTML = html;
        box.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        activeIndex = -1;
        if (status) status.textContent = count ? t('suggestionsAvailable', '{{ count }} suggestions available').replace('{{ count }}', count) : t('noSuggestions', 'No suggestions');
      }

      var run = debounce(function () {
        var term = input.value.trim();
        if (term.length < 2) { close(); return; }

        // Abort the previous request so a slow early keystroke can't overwrite
        // the results for a later one.
        if (controller) controller.abort();
        controller = typeof AbortController !== 'undefined' ? new AbortController() : null;

        var url = '/search/suggest.json?q=' + encodeURIComponent(term)
          + '&resources[type]=product,collection,page,article'
          + '&resources[limit]=4&resources[options][unavailable_products]=last';

        fetch(url, controller ? { signal: controller.signal } : undefined)
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) { if (data) render(data, term); })
          .catch(function () { /* aborted or offline — leave the last result up */ });
      }, 250);

      input.addEventListener('input', run);
      input.addEventListener('focus', function () { if (input.value.trim().length >= 2) run(); });

      input.addEventListener('keydown', function (e) {
        if (box.hidden) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); highlight(activeIndex + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); highlight(activeIndex - 1); }
        else if (e.key === 'Escape') { close(); }
        else if (e.key === 'Enter' && activeIndex > -1) {
          var opt = options()[activeIndex];
          if (opt) { e.preventDefault(); window.location.href = opt.getAttribute('href'); }
        }
      });

      document.addEventListener('click', function (e) {
        if (!root.contains(e.target)) close();
      });
    });
  }

  /* ----------------------------------------------------------
     Services tabs — port of services.tsx (?tab= deep link)
     ---------------------------------------------------------- */
  function initTabs() {
    var root = document.querySelector('[data-tabs]');
    if (!root || root.__wired) return;
    root.__wired = true;
    var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-tab-key]'));
    var panels = Array.prototype.slice.call(document.querySelectorAll('[data-tab-panel]'));

    function show(key, focusTab) {
      buttons.forEach(function (b) {
        var on = b.getAttribute('data-tab-key') === key;
        b.classList.toggle('tab-on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        // Roving tabindex: only the selected tab is in the tab order.
        b.setAttribute('tabindex', on ? '0' : '-1');
        if (on && focusTab) b.focus();
      });
      panels.forEach(function (p) {
        p.style.display = p.getAttribute('data-tab-panel') === key ? '' : 'none';
      });
      var url = new URL(window.location.href);
      url.searchParams.set('tab', key);
      history.replaceState(null, '', url);
    }

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-tab-key]');
      if (!btn) return;
      show(btn.getAttribute('data-tab-key'));
    });

    // Arrow-key navigation between tabs, per the WAI-ARIA tabs pattern.
    root.addEventListener('keydown', function (e) {
      var i = buttons.indexOf(document.activeElement);
      if (i === -1) return;
      var next = null;
      if (e.key === 'ArrowRight') next = (i + 1) % buttons.length;
      else if (e.key === 'ArrowLeft') next = (i - 1 + buttons.length) % buttons.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = buttons.length - 1;
      if (next === null) return;
      e.preventDefault();
      show(buttons[next].getAttribute('data-tab-key'), true);
    });

    var initial = new URL(window.location.href).searchParams.get('tab');
    var valid = buttons.some(function (b) { return b.getAttribute('data-tab-key') === initial; });
    show(valid ? initial : (buttons[0] && buttons[0].getAttribute('data-tab-key')));
  }

  /* ----------------------------------------------------------
     Global delegated events (survive section re-renders)
     ---------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-open]')) { e.preventDefault(); Panel.closeAll(); Drawer.open(); return; }
    if (e.target.closest('[data-cart-close]') || e.target.closest('[data-cart-overlay]')) { e.preventDefault(); Drawer.close(); return; }

    // Generic panel (mobile menu)
    var panelOpen = e.target.closest('[data-panel-open]');
    if (panelOpen) { e.preventDefault(); Panel.open(panelOpen.getAttribute('data-panel-open')); return; }
    if (e.target.closest('[data-panel-close]') || e.target.closest('[data-panel-overlay]')) { e.preventDefault(); Panel.closeAll(); return; }
    // Tapping a link inside an open panel closes it (navigation follows)
    if (e.target.closest('[data-panel] a[href]')) { Panel.closeAll(); }

    // Add to cart
    var addForm = e.target.closest('[data-cart-add]');
    if (addForm) {
      var btn = e.target.closest('[data-add-button]');
      if (btn) {
        e.preventDefault();
        if (btn.disabled) return;
        var root = btn.closest('[data-variant-root]') || addForm;
        var idInput = root.querySelector('[data-variant-id]') || addForm.querySelector('[name="id"]');
        var qtyField = root.querySelector('[data-qty-value]');
        var id = idInput && idInput.value;
        var qty = qtyField ? (parseInt(qtyField.textContent, 10) || 1) : 1;
        if (!id) return;
        btn.setAttribute('data-loading', 'true');
        cartAdd(id, qty).then(function () { btn.removeAttribute('data-loading'); })
          .catch(function () { btn.removeAttribute('data-loading'); });
      }
      return;
    }

    // Cart line quantity / remove (drawer + cart page)
    var dec = e.target.closest('[data-line-dec]');
    var inc = e.target.closest('[data-line-inc]');
    var rm = e.target.closest('[data-line-remove]');
    if (dec || inc || rm) {
      e.preventDefault();
      var node = (dec || inc || rm);
      var lineEl = node.closest('[data-line]');
      if (!lineEl) return;
      // Guard against concurrent /cart/change.js calls: line indexes shift as
      // lines are removed, so an in-flight request would mutate the wrong line.
      if (cartBusy) return;
      var line = parseInt(lineEl.getAttribute('data-line'), 10);
      var current = parseInt(lineEl.getAttribute('data-line-qty'), 10) || 1;
      var next = rm ? 0 : (dec ? current - 1 : current + 1);
      var onCartPage = !!document.querySelector('[data-cart-page]');
      cartBusy = true;
      cartChange(line, next).then(function () {
        cartBusy = false;
        if (onCartPage) window.location.reload();
      }).catch(function () { cartBusy = false; });
      return;
    }
  });

  // Localization selects submit their form on change (noscript users get
  // an explicit Update button instead).
  document.addEventListener('change', function (e) {
    var sel = e.target.closest('[data-localization-select]');
    if (!sel) return;
    var form = sel.closest('form');
    if (form) form.submit();
  });

  document.addEventListener('keydown', function (e) {
    Drawer.trap(e);
    Panel.trap(e);
    if (e.key === 'Escape' && Panel.anyOpen()) Panel.closeAll();
  });

  /* ----------------------------------------------------------
     Boot
     ---------------------------------------------------------- */

  // Behaviour that binds to specific elements found in the DOM (as opposed to
  // the document-level delegated handlers above). Must re-run after the Section
  // Rendering API swaps markup, or the new nodes come back inert.
  function rewire() {
    initCarousel();
    initVariantSelectors();
    initAccordions();
    initTabs();
    initPredictiveSearch();
    initRecommendations();
    initFacets();
  }

  ready(function () {
    initLenis();
    initParallax();
    initCountUp();
    rewire();
  });
})();
